import { getAuth, signOut, type Auth } from 'firebase/auth'

// Keep a reference to the auth instance for reuse
let authInstance: Auth | null = null

// Token caching to reduce API calls
interface TokenCache {
  token: string
  expiryTime: number // Expiry timestamp in milliseconds
}

// Token refresh in progress tracking
let tokenRefreshInProgress = false
let lastRefreshTime = 0
const TOKEN_REFRESH_COOLDOWN = 5000 // 5 seconds in ms
let cachedToken: TokenCache | null = null

/**
 * How a session refresh went. `unauthorized` only when Firebase or the server
 * said the session is no good, which is when to sign out. `unavailable` when
 * they couldn't be asked or didn't answer (offline, a server error): no
 * reason to sign anyone out.
 */
export type SessionRefreshResult = 'refreshed' | 'unauthorized' | 'unavailable'

// Firebase's answers to a token refresh that mean signing in again
const INVALID_SESSION_CODES = new Set([
  'auth/user-token-expired',
  'auth/invalid-user-token',
  'auth/user-disabled',
  'auth/user-not-found',
])

function isInvalidSessionError(error: unknown): boolean {
  const code = (error as { code?: unknown } | null)?.code
  return typeof code === 'string' && INVALID_SESSION_CODES.has(code)
}

/** Whether a rejected $fetch was the server refusing (401/403), not a failed request */
function isRefusedByServer(error: unknown): boolean {
  const e = error as { statusCode?: number, status?: number, response?: { status?: number } } | null
  const status = e?.statusCode ?? e?.status ?? e?.response?.status
  return status === 401 || status === 403
}

// Export a function that doesn't immediately use inject/composables
export function useAuthSession() {
  // Use getAuth() directly to avoid inject() calls
  const getAuthInstance = () => {
    if (authInstance) return authInstance

    try {
      // Only use the composable in component contexts
      if (import.meta.client) {
        authInstance = getAuth()
        return authInstance
      }
    }
    catch (error) {
      console.error('Error getting auth instance:', error)
    }
    return null
  }

  /**
   * Get a valid ID token with caching to minimize API calls
   * Only forces a refresh if the token is expired or forceRefresh=true.
   * Throws Firebase's error when it can't get one
   */
  const requestAuthToken = async (forceRefresh = false): Promise<string | null> => {
    const auth = getAuthInstance()
    if (!auth || !auth.currentUser) return null

    const now = Date.now()

    // Return cached token if it's still valid and no force refresh is requested
    if (!forceRefresh
      && cachedToken
      && now < cachedToken.expiryTime - 5 * 60 * 1000) { // 5 min buffer before expiry
      return cachedToken.token
    }

    // If a refresh is already in progress, wait a bit and use cached token
    if (tokenRefreshInProgress) {
      if (cachedToken) return (cachedToken as TokenCache).token

      // Wait for the other refresh to complete (with timeout)
      let attempts = 0
      while (tokenRefreshInProgress && attempts < 10) {
        await new Promise(resolve => setTimeout(resolve, 300))
        attempts++
      }

      if (cachedToken) return (cachedToken as TokenCache).token
    }

    // Check if we've refreshed too recently (prevent refresh spam)
    if (now - lastRefreshTime < TOKEN_REFRESH_COOLDOWN && cachedToken) {
      return cachedToken.token
    }

    try {
      tokenRefreshInProgress = true
      lastRefreshTime = now

      // Get fresh token
      const token = await auth.currentUser.getIdToken(forceRefresh)

      // Cache the token with expiry time (Firebase tokens typically last 1 hour)
      cachedToken = {
        token,
        expiryTime: now + 55 * 60 * 1000, // 55 minutes - slightly less than the typical 1 hour
      }

      return token
    }
    finally {
      tokenRefreshInProgress = false
    }
  }

  /**
   * As requestAuthToken, but null when no token can be had
   */
  const getAuthToken = async (forceRefresh = false): Promise<string | null> => {
    try {
      return await requestAuthToken(forceRefresh)
    }
    catch (error) {
      console.error('Error getting auth token:', error)
      return null
    }
  }

  /**
   * Check if the current user session is valid without always forcing a token refresh.
   *
   * Also considers the server session cookie's recorded expiry: if the cookie
   * is unknown or within SESSION_REFRESH_MARGIN_MS of expiring, we return
   * false so callers (middleware, fetch plugin) can refresh it proactively
   * before the server starts returning 401s.
   */
  const checkSession = async () => {
    const auth = getAuthInstance()

    if (!auth || !auth.currentUser) {
      return false
    }

    try {
      // Get token but don't force refresh unless needed
      const token = await getAuthToken(false)
      if (!token) return false

      // Server session cookie may have a shorter lifetime (14-day Firebase cap)
      // than the Firebase client session. If it's due for refresh, signal
      // that the caller should renew.
      if (isSessionDueForRefresh()) return false

      return true
    }
    catch (error) {
      console.error('Session invalid:', error)
      return false
    }
  }

  /**
   * Refresh the session, and say why when it didn't work: only
   * `unauthorized` means the user has to sign in again
   */
  const refreshSessionStatus = async (): Promise<SessionRefreshResult> => {
    const auth = getAuthInstance()

    if (!auth || !auth.currentUser) {
      return 'unauthorized'
    }

    let token: string | null
    try {
      // Force refresh the token
      token = await requestAuthToken(true)
    }
    catch (error) {
      console.error('Failed to refresh session:', error)
      return isInvalidSessionError(error) ? 'unauthorized' : 'unavailable'
    }
    if (!token) return 'unauthorized'

    try {
      // Exchange for a new session cookie using $fetch
      const res = await $fetch<{ success: boolean, expiresAt?: number }>('/api/auth/create-session', {
        method: 'POST',
        body: {
          idToken: token,
          expiresIn: 60 * 60 * 24 * 14 * 1000, // 14 days
        },
      })

      if (res?.expiresAt) setStoredSessionExpiresAt(res.expiresAt)

      return 'refreshed'
    }
    catch (error) {
      console.error('Failed to refresh session:', error)
      return isRefusedByServer(error) ? 'unauthorized' : 'unavailable'
    }
  }

  // Refresh the session
  const refreshSession = async () => (await refreshSessionStatus()) === 'refreshed'

  // Handle session logout
  const clearSession = async () => {
    try {
      console.warn('Clearing session...')
      // Call logout endpoint to clear cookie
      await $fetch('/api/auth/logout', { method: 'POST' })

      // Sign out from Firebase if available
      const auth = getAuthInstance()
      if (auth) {
        await signOut(auth)
      }

      // Clear all module-level state on logout
      cachedToken = null
      authInstance = null
      tokenRefreshInProgress = false
      lastRefreshTime = 0
      clearStoredSessionExpiresAt()
      return true
    }
    catch (error) {
      console.error('Error clearing session:', error)
      return false
    }
  }

  return {
    checkSession,
    refreshSession,
    refreshSessionStatus,
    clearSession,
    getAuthToken, // Expose this for use in other places
  }
}
